import React, { useMemo } from 'react';
import { Card, Row, Col, Table, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { MedicalDocumentsGallery } from '#components';
// import { HealthReportsGallery } from '#components'; // Temporarily disabled - health reports not included in share links
import PublicBiomarkersView from './PublicBiomarkersView';
import PublicSkinMolesView from './PublicSkinMolesView';
import { flattenHealthInterviewDataEntries } from '#utils/healthInterviewDisplay';

const PublicSharedDataView = ({ data = {}, allowedScopes = {}, token }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const hasData = (scope) => {
    if (!allowedScopes[scope] || !data[scope]) {
      return false;
    }
    
    // For biomarkers, check if it's an array with items or object with float_values/string_values
    if (scope === 'biomarkers') {
      const biomarkers = data[scope];
      if (Array.isArray(biomarkers)) {
        return biomarkers.length > 0;
      }
      // Fallback: check if it's an object with float_values or string_values
      const hasFloatValues = biomarkers.float_values && Object.keys(biomarkers.float_values).length > 0;
      const hasStringValues = biomarkers.string_values && Object.keys(biomarkers.string_values).length > 0;
      return hasFloatValues || hasStringValues;
    }
    
    // For skin_moles, check if it's an array with items or object with results/user_moles array
    if (scope === 'skin_moles') {
      const skinMoles = data[scope];
      if (Array.isArray(skinMoles)) {
        return skinMoles.length > 0;
      }
      // Check if it's an object with results array (from backend serializer)
      if (skinMoles && typeof skinMoles === 'object') {
        if (Array.isArray(skinMoles.results)) {
          return skinMoles.results.length > 0;
        }
        if (Array.isArray(skinMoles.user_moles)) {
          return skinMoles.user_moles.length > 0;
        }
      }
      return false;
    }
    
    // For other scopes, check if it's an array with items
    return Array.isArray(data[scope]) && data[scope].length > 0;
  };

  // Filter and sort medical documents to only include confirmed documents
  // Sort by document date (not creation date) in descending order (newest first)
  const sortedMedicalDocuments = useMemo(() => {
    if (!hasData('medical_documents')) {
      return [];
    }
    // Filter to only include documents with confirmed === true
    const confirmedDocuments = data.medical_documents.filter(doc => doc.confirmed === true);
    return confirmedDocuments.sort((a, b) => {
      const dateA = moment(a.date);
      const dateB = moment(b.date);
      // Return negative if dateA is after dateB (newer first)
      return dateB.diff(dateA);
    });
  }, [data.medical_documents]);

  // Health reports are temporarily disabled - not included in share links
  // Sort health reports by creation date in descending order (newest first)
  // const sortedHealthReports = useMemo(() => {
  //   if (!hasData('health_reports')) {
  //     return [];
  //   }
  //   return [...data.health_reports].sort((a, b) => {
  //     const dateA = moment(a.created);
  //     const dateB = moment(b.created);
  //     // Return negative if dateA is after dateB (newer first)
  //     return dateB.diff(dateA);
  //   });
  // }, [data.health_reports]);

  // Sort health interviews by creation date in descending order (newest first)
  const sortedHealthInterviews = useMemo(() => {
    if (!hasData('health_interviews')) {
      return [];
    }
    return [...data.health_interviews].sort((a, b) => {
      const dateA = moment(a.created_at || a.created);
      const dateB = moment(b.created_at || b.created);
      // Return negative if dateA is after dateB (newer first)
      return dateB.diff(dateA);
    });
  }, [data.health_interviews]);

  return (
    <div>
      {/* Health Reports - temporarily disabled, not included in share links */}
      {/* {hasData('health_reports') && (
        <Card border='0' className='shadow mb-4'>
          <Card.Header>
            <Card.Title className='mb-0'>{t('shared-access.public-view.health-reports')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <HealthReportsGallery data={sortedHealthReports} readOnly={true} />
          </Card.Body>
        </Card>
      )} */}

      {/* Biomarkers - first if selected */}
      {hasData('biomarkers') && (
        <Card border='0' className='shadow mb-4'>
          <Card.Header>
            <Card.Title className='mb-0'>{t('shared-access.public-view.biomarkers')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <PublicBiomarkersView 
              biomarkersData={data.biomarkers} 
              biomarkerMapping={data.biomarker_mapping || data.biomarkers_mapping}
              token={token}
            />
          </Card.Body>
        </Card>
      )}

      {/* Skin Moles */}
      {hasData('skin_moles') && (
        <Card border='0' className='shadow mb-4'>
          <Card.Header>
            <Card.Title className='mb-0'>{t('shared-access.public-view.skin-moles')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <PublicSkinMolesView
              molesData={data.skin_moles}
              moleImagesData={
                data.mole_images || 
                data.skin_mole_images || 
                data.skin_moles_images ||
                []
              }
              bodyPartsData={data.body_parts || {}}
            />
          </Card.Body>
        </Card>
      )}

      {/* Health Interviews */}
      {hasData('health_interviews') && (
        <Card border='0' className='shadow mb-4'>
          <Card.Header>
            <Card.Title className='mb-0'>{t('shared-access.public-view.health-interviews')}</Card.Title>
          </Card.Header>
          <Card.Body>
            {sortedHealthInterviews.length > 0 ? (() => {
              // Show only the latest health interview
              const latestInterview = sortedHealthInterviews[0];
              const interviewData = latestInterview.data || latestInterview;
              const dataEntries =
                interviewData && typeof interviewData === 'object'
                  ? flattenHealthInterviewDataEntries(interviewData)
                  : [];

              // Helper function to translate field names
              const translateFieldName = (key) => {
                // Handle fields with -Comment suffix (e.g., medications-Comment)
                if (key.endsWith('-Comment')) {
                  const baseKey = key.replace('-Comment', '');
                  const baseFieldName = translateFieldName(baseKey);
                  return `${baseFieldName} - ${t('shared-access.public-view.comment')}`;
                }
                
                const translationKey = `shared-access.public-view.health-interview-fields.${key}`;
                const translated = t(translationKey);
                // If translation exists, use it; otherwise use the key
                return translated !== translationKey ? translated : key;
              };

              // Helper function to translate field values
              const translateFieldValue = (key, value) => {
                if (!value || value === null || value === undefined) {
                  return '-';
                }

                // Handle array values (e.g., conditions, symptoms)
                if (Array.isArray(value)) {
                  return value.map(v => translateFieldValue(key, v)).join(', ');
                }

                // Handle string values
                const valueStr = String(value);
                
                // Try to translate based on field key
                const translationKey = `shared-access.public-view.health-interview-values.${key}.${valueStr}`;
                const translated = t(translationKey);
                
                // If translation exists, use it; otherwise try generic value translation
                if (translated !== translationKey) {
                  return translated;
                }

                // Try generic value translation (e.g., for gender: MALE, FEMALE)
                const genericTranslationKey = `shared-access.public-view.health-interview-values.${valueStr}`;
                const genericTranslated = t(genericTranslationKey);
                if (genericTranslated !== genericTranslationKey) {
                  return genericTranslated;
                }

                return valueStr;
              };

              return (
                <div>
                  <div className='mb-3'>
                    <Badge bg='success' className='me-2'>
                      {t('shared-access.public-view.latest')}
                    </Badge>
                    <span className='text-muted'>
                      {t('shared-access.public-view.interview-date')}: {moment(latestInterview.created_at || latestInterview.created).format(
                        currentLanguage === 'pl' ? 'DD.MM.YYYY HH:mm' : 'MMM D, YYYY HH:mm'
                      )}
                    </span>
                  </div>
                  {dataEntries.length > 0 ? (
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th style={{ width: '40%' }}>{t('shared-access.public-view.interview-questions')}</th>
                          <th style={{ width: '60%' }}>{t('shared-access.public-view.interview-answers')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataEntries.map(([key, value]) => (
                          <tr key={key}>
                            <td>
                              <strong>{translateFieldName(key)}</strong>
                            </td>
                            <td>
                              {translateFieldValue(key, value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className='text-muted text-center mb-0'>
                      {t('shared-access.public-view.no-interview-data')}
                    </p>
                  )}
                </div>
              );
            })() : (
              <p className='text-muted text-center mb-0'>
                {t('shared-access.public-view.no-interviews-available')}
              </p>
            )}
          </Card.Body>
        </Card>
      )}

      {/* {hasData('health_summaries') && (
        <Card border='0' className='shadow mb-4'>
          <Card.Header>
            <Card.Title className='mb-0'>{t('shared-access.public-view.health-summaries')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <p className='text-muted'>{t('shared-access.public-view.no-summaries-display')}</p>
            <pre className='bg-light p-3' style={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(data.health_summaries, null, 2)}
            </pre>
          </Card.Body>
        </Card>
      )} */}

      {/* Medical Documents - always at the end if selected */}
      {sortedMedicalDocuments.length > 0 && (
        <Card border='0' className='shadow mb-4'>
          <Card.Header>
            <Card.Title className='mb-0'>{t('shared-access.public-view.medical-documents')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <MedicalDocumentsGallery 
              data={sortedMedicalDocuments} 
              hideAI={false}
              hideRemoveButton={true}
              onRemove={() => {}} // Empty function for read-only mode
            />
          </Card.Body>
        </Card>
      )}

      {sortedMedicalDocuments.length === 0 &&
        // !hasData('health_reports') && // Health reports temporarily disabled
        !hasData('health_interviews') &&
        // !hasData('health_summaries') &&
        !hasData('biomarkers') &&
        !hasData('skin_moles') && (
          <Card border='0' className='shadow'>
            <Card.Body>
              <p className='text-muted text-center mb-0'>
                {t('shared-access.public-view.no-data-available')}
              </p>
            </Card.Body>
          </Card>
        )}
    </div>
  );
};

export default PublicSharedDataView;

